/**
 * Rect が layout 済みか判定 (jsdom や non-visible 要素の width/height=0 を弾く)。
 */
export function isRectLaidOut(rect: { width: number; height: number }): boolean {
  return rect.width > 0 && rect.height > 0;
}

/**
 * 案C の zoom OUT 側ロジック: zoomOrigin を変更せず、pan を zoom 比でスケールする。
 *
 * 数学的性質: transformOrigin 固定 + pan_new = pan_old * (newZoom / currentZoom) のとき、
 * 視野中央が zoom 前後で画面上同じ位置に写り続ける。
 *
 * 呼び出し側で IN/OUT 判定は行う想定。関数自体は pure scale。
 * currentZoom=0 の divide-by-zero 対策として 1e-6 で clamp。
 */
export function zoomOutScalePan(
  currentZoom: number,
  newZoom: number,
  currentPan: { x: number; y: number },
): { x: number; y: number } {
  const safeCurrentZoom = Math.max(currentZoom, 1e-6);
  const ratio = newZoom / safeCurrentZoom;
  return {
    x: currentPan.x * ratio,
    y: currentPan.y * ratio,
  };
}

/**
 * 案D-1: IN zoom イベントごとに cursor 位置を新 pivot にスナップし、
 * 見た目維持 pan を直接算出する self-contained な純関数。
 *
 * 前提: wrapperRect = wrapper.getBoundingClientRect() (現 state の transform 後)
 *
 * 計算:
 *  1. 現 state から cursor 論理位置 (lx, ly) を逆変換
 *  2. newPivot = cursor 論理位置を % に変換
 *  3. newPivot_px = lx (スナップしたので一致) → newPan = cursor - lx
 *
 * 特徴: self-contained。cursor 位置と現 state のみで次 pan を決定するため、
 * 前 pivot を参照しない。累積誤差が発生しない。
 * rect.width=0 は jsdom 対応で currentPivot/currentPan をそのまま返す。
 */
export function snapPivotToCursor(
  wrapperRect: { left: number; top: number; width: number; height: number },
  cursorX: number,
  cursorY: number,
  currentPivot: { x: number; y: number },
  currentZoom: number,
  currentPan: { x: number; y: number },
): { pivot: { x: number; y: number }; pan: { x: number; y: number } } {
  if (!isRectLaidOut(wrapperRect)) {
    return { pivot: currentPivot, pan: currentPan };
  }
  const W = wrapperRect.width / currentZoom;
  const H = wrapperRect.height / currentZoom;

  const ox_px_relative = (currentPivot.x / 100) * W;
  const oy_px_relative = (currentPivot.y / 100) * H;

  const X0 = wrapperRect.left - ox_px_relative * (1 - currentZoom) - currentPan.x;
  const Y0 = wrapperRect.top - oy_px_relative * (1 - currentZoom) - currentPan.y;

  const ox_px = X0 + ox_px_relative;
  const oy_px = Y0 + oy_px_relative;

  const lx = ox_px + (cursorX - ox_px - currentPan.x) / currentZoom;
  const ly = oy_px + (cursorY - oy_px - currentPan.y) / currentZoom;

  const newPivot = {
    x: ((lx - X0) / W) * 100,
    y: ((ly - Y0) / H) * 100,
  };

  // newPivot を cursor 論理位置にスナップしたので newPivot_px = lx, ly
  // sx = lx + newPan.x、cursor 位置に合わせるため newPan = cursor - lx
  const newPan = {
    x: cursorX - lx,
    y: cursorY - ly,
  };

  return { pivot: newPivot, pan: newPan };
}
